'use client'

import { StyledIbtroziForRos1 } from './page.styles'
import { Box, Typography, useTheme, Stack, Divider } from '@mui/material'
import { Button } from '@biolumina/ui/components/button/button'
import { Link } from '@biolumina/ui/components/link/link'
import { Callout } from '@biolumina/ui/components/callout/callout'
import { ChartBox } from '@biolumina/ui/components/chartBox/chartBox'
import { Span } from '@biolumina/ui/components/span/span'
import { Footnote } from '@biolumina/ui/components/footnote/footnote'
import ListItem from '@biolumina/ui/components/listItem/listItem'
import List from '@biolumina/ui/components/list/list'
import { PageDivider } from '@biolumina/ui/components/pageDivider/pageDivider'
import { Anchor } from '@biolumina/ui/components/anchor/anchor'
import { usePageData } from '@biolumina/ui/hooks/pageData.hook'
import { UrgencyChartSection } from './UrgencyChartSection'
import { BaseImage } from '@biolumina/ui/components/baseImage/baseImage'
import { PrimaryGoalResults } from './PrimaryGoalResults'
import { ElementClinicalStudies } from './ElementClinicalStudies'
import { ImageBreakerCallout } from '@biolumina/ui/components/imageBreakerCallout/imageBreakerCallout'
import Break from '@biolumina/ui/components/break/break'
import { SummarySection } from '@biolumina/ui/components/summarySection/summarySection'

// const refs = (
//   <>
//     <strong>References: 1.</strong> Ibtrozi. Prescribing information. Nuvation
//     Bio; 2025.
//   </>
// )

export default function IbtroziForRos1() {
  const theme = useTheme()
  const { anchors } = usePageData()

  return (
    <StyledIbtroziForRos1>
      <Anchor id={anchors[0]}>
        <Typography variant="h2">How was IBTROZI studied?</Typography>

        <Typography variant="body1">
          <Span fontWeight={800}>
            IBTROZI was studied across 2 clinical studies, TRUST-I and TRUST-II,
            in a large advanced ROS1+ NSCLC population.
          </Span>{' '}
          The clinical benefit of IBTROZI was assessed in 270 patients.
        </Typography>

        <ElementClinicalStudies />

        <Typography variant="body1" pb={2}>
          In each study, some people were given IBTROZI as their{' '}
          <strong>first targeted TKI therapy,</strong> while others had received
          a <strong>targeted TKI therapy</strong> prior to IBTROZI.
          <br />
        </Typography>

        <Typography variant="body1" fontWeight={800}>
          TRUST-I and TRUST-II had the same goals
        </Typography>

        <ChartBox mt={2.5} sx={{ marginBottom: '60px' }}>
          <Stack pt={1} px={1} direction="row" alignItems="flex-start">
            <BaseImage
              alt=""
              src="/images/triangle-icon.svg"
              width="45"
              sx={{ width: { xs: '27px', md: '45px' } }}
            />
            <Box mt={{ xs: 0.5, md: 1.5 }} ml={{ xs: 1, md: 2 }}>
              <Typography color="primary.main" fontWeight={800}>
                Primary goal:
              </Typography>
              <Typography variant="body1">
                Measured the{' '}
                <Span fontWeight={900}>overall response rate (ORR),</Span> which
                means how many people responded to IBTROZI, including:
              </Typography>
              <List mb={0}>
                <ListItem>
                  Partial response: a reduction in tumor size but hasn&rsquo;t
                  entirely disappeared
                </ListItem>
                <ListItem>
                  Complete response: a disappearance of tumor(s), but it does
                  not mean the cancer is cured. Cancer cells may still be in the
                  body, but may be too small to detect
                </ListItem>
              </List>
            </Box>
          </Stack>

          <Divider
            sx={{ bgcolor: 'primary.main', margin: theme.spacing(4, 0) }}
          />

          <Stack pb={1} px={1} direction="row" alignItems="flex-start">
            <BaseImage
              alt=""
              src="/images/triangle-icon.svg"
              width="45"
              sx={{ width: { xs: '27px', md: '45px' } }}
            />
            <Box mt={{ xs: 0.5, md: 1.5 }} ml={{ xs: 1, md: 2 }}>
              <Typography color="primary.main" fontWeight={800}>
                Secondary goal:
              </Typography>
              <Typography variant="body1" mb={0}>
                Measured the{' '}
                <Span fontWeight={900}>duration of response (DOR),</Span> which
                is the amount of time that a treatment keeps working with
                positive effects before the cancer starts to grow again
              </Typography>
            </Box>
          </Stack>
        </ChartBox>
      </Anchor>

      <PageDivider />

      <Anchor id={anchors[1]}>
        <Typography variant="h2" color="primary.main">
          How well did IBTROZI work?
        </Typography>
        <Typography variant="body1">
          In the 157 people given IBTROZI as their{' '}
          <Span fontWeight={800}>first targeted TKI therapy</Span>
        </Typography>

        <PrimaryGoalResults
          studyData={[
            {
              id: 1,
              title: 'TRUST-I',
              percentage: '90%',
              description: 'saw their tumors<br/>shrink or disappear',
              details: '(93 out of 103 people)',
            },
            {
              id: 2,
              title: 'TRUST-II',
              percentage: '85%',
              description: 'saw their tumors<br/>shrink or disappear',
              details: '(46 out of 54 people)',
            },
          ]}
        />

        <Typography variant="h2" color="primary.main">
          Secondary goal results (DOR)
        </Typography>

        <Typography mb={0} fontWeight={800}>
          In TRUST-I:
        </Typography>
        <List>
          <ListItem mb={0}>
            At the time of the first analysis, <Span fontWeight={800}>72%</Span>{' '}
            of people (74 out of 103) taking IBTROZI were still seeing a
            response for 12 months or longer. The longest observed DOR was{' '}
            <Span fontWeight={800}>46.9 months</Span>, with some patients
            continuing to respond
          </ListItem>
          <ListItem>
            <Span fontWeight={800}>57.9 months</Span> was the longest observed
            DOR at a later <Span fontWeight={800}>long-term follow-up</Span>
          </ListItem>
        </List>

        <Typography mb={0} fontWeight={800}>
          In TRUST-II:
        </Typography>
        <List>
          <ListItem>
            At the time of the first analysis, <Span fontWeight={800}>63%</Span>{' '}
            of people (34 out of 54) taking IBTROZI were seeing a response for
            over 12 months. The longest observed DOR was{' '}
            <Span fontWeight={800}>30.4 months</Span>, with some people
            continuing to respond
          </ListItem>
        </List>

        <Typography variant="body1">
          In the 113 people who had taken{' '}
          <Span fontWeight={800}>
            prior targeted TKI therapy before IBTROZI
          </Span>
        </Typography>

        <PrimaryGoalResults
          studyData={[
            {
              id: 1,
              title: 'TRUST-I',
              percentage: '52%',
              description: 'saw their tumors&nbsp;shrink',
              details: '(34 out of 66 people)',
            },
            {
              id: 2,
              title: 'TRUST-II',
              percentage: '62%',
              description: 'saw their tumors<br/>shrink or disappear',
              details: '(29 out of 47 people)',
            },
          ]}
        />

        <Typography variant="h2" color="primary.main">
          Secondary goal results (DOR)
        </Typography>

        <Typography mb={0} fontWeight={800}>
          In TRUST-I:
        </Typography>
        <List>
          <ListItem mb={0}>
            At the time of the first analysis, <Span fontWeight={800}>44%</Span>{' '}
            of people (29 out of 66) taking IBTROZI were still seeing a response
            for 12 months or longer. The longest observed DOR was{' '}
            <Span fontWeight={800}>38.7 months</Span>, with some patients
            continuing to respond
          </ListItem>
          <ListItem>
            <Span fontWeight={800}>46.9 months</Span> was the longest observed
            DOR at a later <Span fontWeight={800}>long-term follow-up</Span>
          </ListItem>
        </List>

        <Typography mb={0} fontWeight={800}>
          In TRUST-II:
        </Typography>
        <List>
          <ListItem>
            At the time of the first analysis, <Span fontWeight={800}>45%</Span>{' '}
            of people (21 out of 47) taking IBTROZI were seeing a response for
            over 12 months. The longest observed DOR was{' '}
            <Span fontWeight={800}>30.4 months</Span>, with some people
            continuing to respond
          </ListItem>
        </List>

        <ImageBreakerCallout mb={6} mobileLong>
          <Typography
            variant="h3"
            mb={0}
            maxWidth={600}
            color="#062059"
            fontSize={{ xs: '1rem', sm: '0.85rem', md: '1.125rem' }}
            fontWeight={{ xs: 'normal', md: '700' }}
          >
            Results may differ from person to person. Speak with your healthcare
            provider if you have any questions.
            <br></br>
            <br></br>
            <Span fontWeight={400}>
              Information found online and through AI may provide general
              information that may not apply to your specific medical needs.
              Please consult your healthcare provider regarding diagnosis,
              symptoms, or treatment decisions.
            </Span>
          </Typography>
        </ImageBreakerCallout>
      </Anchor>

      <Anchor id={anchors[2]}>
        <Typography variant="h2">
          Safety considerations while taking IBTROZI
        </Typography>

        <Typography variant="body1">
          You may experience side effects while taking IBTROZI. If side effects
          do occur, talk to your healthcare provider right away.
        </Typography>

        <UrgencyChartSection />

        <Typography variant="body1">
          Your healthcare provider can decrease your dose, temporarily stop, or
          completely stop your treatment with IBTROZI depending on the severity
          of your symptoms. During clinical trials, about 93% of people were
          able to stay on treatment with IBTROZI. Each person's body reacts
          differently to treatment, so side effects can vary from one individual
          to another.
        </Typography>
        <Footnote className="defs" marginBottom="40px">
          DOR=duration of response; NSCLC=non-small cell lung cancer; ROS1+=ROS
          proto-oncogene 1–positive; TKI=tyrosine kinase inhibitor.
        </Footnote>

        <Stack
          pt={2}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="center"
          spacing={{ xs: 2, md: 3 }}
          margin={0}
          mb={4}
        >
          <Callout
            className="tertiary"
            cta={
              <Link
                component={Button}
                className="flex"
                trackingId="taking"
                sx={{
                  outline: 'none',
                  color: 'black',
                  '&:hover': {
                    outline: 'none',
                    backgroundColor: '#062059',
                    color: 'white',
                    svg: { fill: 'white' },
                  },
                }}
                href="/taking-ibtrozi"
              >
                Read more
              </Link>
            }
          >
            <Typography>
              <strong>How to take IBTROZI</strong>
            </Typography>
          </Callout>

          <Callout
            className="secondary"
            cta={
              <Link
                component={Button}
                className="flex"
                trackingId="brochure"
                sx={{
                  outline: 'none',
                  color: 'black',
                  '&:hover': {
                    outline: 'none',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    svg: { fill: 'white' },
                  },
                }}
                href="/pdf/IBTROZI-Patient-Brochure.pdf"
                target="_blank"
              >
                Download brochure
              </Link>
            }
          >
            <Typography>
              <strong>
                See the IBTROZI Brochure for tips on side effect management
              </strong>
            </Typography>
          </Callout>
        </Stack>
      </Anchor>
      <Anchor id={anchors[3]}>
        <SummarySection
          sx={{ my: 8 }}
          label={
            <>
              Summary:
              <Break md={false} />
              About IBTROZI
            </>
          }
        >
          IBTROZI<sup>&reg;</sup> (taletrectinib) was studied in 2 clinical trials of people with advanced ROS1+
          NSCLC that spread within the chest or to other parts of the body,
          TRUST-I and TRUST-II. These studies measured how many people&rsquo;s
          tumors responded to treatment and how long those responses lasted.
          Most people using IBTROZI as their first targeted treatment, or TKI,
          saw their tumors shrink and some had their tumors disappear. This
          includes 90% of people in TRUST-I and 85% of people in TRUST-II. Many
          people continued responding for a year or even several years. More
          than 50% of people who were treated with a different TKI before
          starting IBTROZI saw their tumors shrink.
          <br></br>
          <br></br>
          You may experience side effects while taking IBTROZI. If side effects
          do occur, talk to your healthcare provider right away. For more
          information on risks see{' '}
          <Link href="/#isi" color="primary.main" target="_blank">
            IBTROZI.com/#isi
          </Link>
          .
        </SummarySection>
      </Anchor>
    </StyledIbtroziForRos1>
  )
}
